/**
 * ExportLinesDialog.jsx — export-first flow for the Lines tab (founder request,
 * verbatim: "i want a flow: export xlsx/csv then filters").
 *
 * Inverse of the old "Export CSV" button, which just dumped whatever the page's
 * OWN filters currently showed. Here ONE dialog: pick a format (XLSX or CSV,
 * XLSX default) -> pick filters (seeded from the page's current filter state,
 * but never trapped by it — "All lines" is one click away) -> download.
 *
 * Driving use case: a tenant with 392 capital calls, where the old report XLSX
 * returned 1-2 of them because its Transactions sheet was a 100-row-capped
 * "recent activity" list. This component filters `allLines` — the FULL feed,
 * never a pre-filtered `visibleLines` — and applies NO row cap and NO implicit
 * date window (dateFrom/dateTo are opt-in and empty by default). See
 * `frontend/src/__tests__/ExportLinesDialog.test.jsx` for the regression test.
 *
 * This component is pure presentational+logic: the page owns when it's open
 * (mount/unmount it) and passes every dependency in as a prop. It reuses the
 * SAME identity/matching helpers the page uses — applyLinesFilters/foldForMatch/
 * foldTight from lib/ledgerFilters, resolveGroup/canonFund/canonInvestor as
 * passed-in props — nothing here reimplements fold-identity matching.
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import api, { getBlobErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import DatePresets from "@/components/ui/date-presets";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { applyLinesFilters, foldForMatch, foldTight, sortLines } from "@/lib/ledgerFilters";
import { buildLinesCsv, commitmentRows, displayName, lineDocumentName } from "@/lib/linesDisplay";

const EMPTY_EXPORT_FILTERS = {
  search: "", portal: "", fund: "", investor: "", group: "", type: "", dateFrom: "", dateTo: "",
};

const TYPE_OPTIONS = [
  ["", "All", "export-type-all"],
  ["capital_call_event_amount", "Capital Calls", "export-type-calls"],
  ["distribution_event_amount", "Distributions", "export-type-dists"],
];

function todayLocalDateStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Same bidirectional dual-fold substring check LedgerDashboardPage.jsx's
// private foldSearchMatches uses for commitment rows (which never go through
// applyLinesFilters) — not exported there, so mirrored here on the same two
// reusable primitives (foldForMatch/foldTight) rather than reimplemented.
function foldSearchMatches(haystack, needle) {
  return (
    foldForMatch(haystack).includes(foldForMatch(needle)) ||
    foldTight(haystack).includes(foldTight(needle))
  );
}

// ── XLSX row-shaping — mirrors buildLinesCsv's per-row semantics (linesDisplay.js)
// column-for-column, just emitting a raw value array per row instead of an
// escaped CSV string, since the server writes these directly into a workbook.
const EXPORT_COLUMNS = ["Portal", "Fund", "Member", "Group", "Date", "Type", "Amount", "Currency", "Document", "Tags"];
const ROW_TYPE_LABEL = { capital_call_event_amount: "Call", distribution_event_amount: "Distribution" };
const ROW_TYPE_SIGN = { capital_call_event_amount: -1, distribution_event_amount: 1 };

function safeCurrencyCode(currency) {
  const normalized = String(currency || "").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(normalized) ? normalized : "USD";
}

function tagsLabel(line) {
  const tags = [];
  if (line.discarded) tags.push("discarded");
  if (line.excluded) tags.push("excluded");
  return tags.join(" ");
}

function buildExportRows(lines, commitments, { fundMap, memberMap, resolveGroup }) {
  const rows = [];
  for (const line of lines || []) {
    const knownType = Object.prototype.hasOwnProperty.call(ROW_TYPE_LABEL, line.type);
    const typeLabel = knownType ? ROW_TYPE_LABEL[line.type] : String(line.type || "");
    const sign = knownType ? ROW_TYPE_SIGN[line.type] : 1;
    const amount = line.amount == null ? "" : Math.abs(Number(line.amount)) * sign;
    rows.push([
      line.portal || "",
      displayName(line.fund, fundMap),
      displayName(line.investor, memberMap),
      resolveGroup(line.investor) || "",
      line.event_date ? String(line.event_date).slice(0, 10) : "",
      typeLabel,
      amount,
      safeCurrencyCode(line.currency),
      lineDocumentName(line, { memberMap, resolveGroup }),
      tagsLabel(line),
    ]);
  }
  for (const c of commitments || []) {
    const amount = c.amount == null ? "" : Math.abs(Number(c.amount));
    rows.push([
      c.portal || "",
      displayName(c.fund, fundMap),
      displayName(c.investor, memberMap),
      resolveGroup(c.investor) || "",
      "",
      "Commitment (total)",
      amount,
      safeCurrencyCode(c.currency),
      "",
      "",
    ]);
  }
  return rows;
}

// Radix-style Select items can't carry an empty value, so "All ..." uses a sentinel.
const ALL = "__all__";
const toSel = (v) => (v ? v : ALL);
const fromSel = (v) => (v === ALL ? "" : v);

export default function ExportLinesDialog({
  allLines,
  portals,
  groups,
  fundMap,
  memberMap,
  resolveGroup,
  canonFund,
  canonInvestor,
  initialFilters,
  sort = "date_desc",
  portalOptions,
  fundOptions,
  investorOptions,
  onClose,
}) {
  const lines = allLines || [];
  const gList = groups || [];
  const fMap = fundMap || {};
  const mMap = memberMap || {};
  const resolveGroupFn = resolveGroup || (() => "");
  const canonFundFn = canonFund || ((v) => v);
  const canonInvestorFn = canonInvestor || ((v) => v);

  const [step, setStep] = useState("format"); // "format" | "filters"
  const [format, setFormat] = useState("xlsx"); // XLSX is the default — the whole point of this feature
  const [filters, setFilters] = useState(() => ({ ...EMPTY_EXPORT_FILTERS, ...(initialFilters || {}) }));
  const [downloading, setDownloading] = useState(false);

  const handleClose = () => {
    if (onClose) onClose();
  };

  const handleResetFilters = () => setFilters({ ...EMPTY_EXPORT_FILTERS });

  // Slug -> display title map, same derivation LedgerDashboardPage.jsx does
  // from `portals`, so free-text search can match the fund title a client
  // SEES, not just its slug (applyLinesFilters' fundDisplayNames arg).
  const fundDisplayNames = useMemo(() => {
    const map = {};
    for (const p of portals || []) {
      for (const f of (p && p.funds) || []) {
        if (f.fund_name && f.fund_display_name) map[f.fund_name] = f.fund_display_name;
      }
    }
    return map;
  }, [portals]);

  // Filtered EVENT lines (never commitments — those are appended separately
  // below, mirroring the page's CSV export). Applied over the FULL `allLines`
  // feed, never a pre-filtered subset — no row cap, no implicit date window:
  // dateFrom/dateTo only narrow the result when the client explicitly sets them.
  const filteredLines = useMemo(() => {
    let result = applyLinesFilters(lines, filters, gList, fundDisplayNames);
    if (filters.dateFrom || filters.dateTo) {
      result = result.filter((l) => {
        const d = l.event_date ? String(l.event_date).slice(0, 10) : "";
        if (!d) return false; // can't verify an undated line falls in an explicit window
        if (filters.dateFrom && d < filters.dateFrom) return false;
        if (filters.dateTo && d > filters.dateTo) return false;
        return true;
      });
    }
    // Same order the table shows — the page's Sort dropdown carries through.
    return sortLines(result, sort);
  }, [lines, filters, gList, fundDisplayNames, sort]);

  // Commitment (total) snapshot rows — mirrors LedgerDashboardPage.jsx's
  // handleExportLinesCsv exactly: canonicalized into the same identity space
  // as allLines, excluded entirely when a type filter is active (a commitment
  // snapshot is neither a Call nor a Distribution), otherwise filtered by the
  // same portal/fund/investor/group/search axes.
  const commitments = useMemo(() => {
    let list = commitmentRows(portals).map((c) => ({
      ...c,
      fund: canonFundFn(c.fund),
      investor: canonInvestorFn(c.investor),
    }));
    if (filters.type) return [];
    const selectedGroupName = filters.group
      ? (gList.find((g) => g.group_id === filters.group)?.name || "")
      : "";
    return list.filter((c) => {
      if (filters.portal && c.portal !== filters.portal) return false;
      if (filters.fund && foldTight(c.fund) !== filters.fund) return false;
      if (filters.investor && foldTight(c.investor) !== filters.investor) return false;
      if (filters.group && resolveGroupFn(c.investor) !== selectedGroupName) return false;
      if (filters.search) {
        const q = filters.search;
        const matches =
          foldSearchMatches(c.portal, q) ||
          foldSearchMatches(c.fund, q) ||
          foldSearchMatches(fMap[foldTight(c.fund)] || "", q) ||
          foldSearchMatches(c.investor, q);
        if (!matches) return false;
      }
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portals, filters, gList, resolveGroupFn, fMap, canonFundFn, canonInvestorFn]);

  const exportBase = filters.type === "capital_call_event_amount" ? "ledger_capital_calls" : "ledger_cashflows";
  const buildFilename = (ext) => `${exportBase}_${todayLocalDateStr()}.${ext}`;

  const handleExport = async () => {
    if (format === "csv") {
      const csv = "﻿" + buildLinesCsv({ lines: filteredLines, commitments, fundMap: fMap, memberMap: mMap, resolveGroup: resolveGroupFn });
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = buildFilename("csv");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      handleClose();
      return;
    }

    setDownloading(true);
    try {
      const rows = buildExportRows(filteredLines, commitments, { fundMap: fMap, memberMap: mMap, resolveGroup: resolveGroupFn });
      const filename = buildFilename("xlsx");
      const res = await api.post(
        "/ledger/export-xlsx",
        { sheet_name: "Cashflows", columns: EXPORT_COLUMNS, rows, filename },
        { responseType: "blob" }
      );
      const blob = res.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Export downloaded");
      handleClose();
    } catch (err) {
      toast.error(await getBlobErrorMessage(err, "Failed to export"));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Export Lines</DialogTitle>
        </DialogHeader>

        {step === "format" ? (
          <div className="space-y-4">
            <p className="text-xs text-gray-500">Choose a file format.</p>

            <div className="space-y-2">
              <label className="flex items-start gap-2 rounded-md border border-gray-200 p-3 cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="export-format"
                  value="xlsx"
                  checked={format === "xlsx"}
                  onChange={() => setFormat("xlsx")}
                  className="mt-0.5"
                  aria-label="XLSX"
                />
                <span>
                  <span className="block text-sm font-medium">XLSX</span>
                  <span className="block text-xs text-gray-500">Excel workbook — recommended, no row limit</span>
                </span>
              </label>

              <label className="flex items-start gap-2 rounded-md border border-gray-200 p-3 cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="export-format"
                  value="csv"
                  checked={format === "csv"}
                  onChange={() => setFormat("csv")}
                  className="mt-0.5"
                  aria-label="CSV"
                />
                <span className="block text-sm font-medium">CSV</span>
              </label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="button" onClick={() => setStep("filters")}>
                Next
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <button
              type="button"
              onClick={handleResetFilters}
              data-testid="export-reset-filters"
              className="text-xs font-medium text-indigo-600 hover:underline"
            >
              All lines (clear filters)
            </button>

            <div className="space-y-1.5">
              <Label htmlFor="export-search">Search</Label>
              <Input
                id="export-search"
                value={filters.search}
                onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
                placeholder="Search fund, member, portal, document..."
              />
            </div>

            <div className="space-y-1.5">
              <Label>Type</Label>
              <div className="flex items-center rounded-md border border-gray-200 bg-white p-0.5 w-fit">
                {TYPE_OPTIONS.map(([val, label, tid]) => (
                  <button
                    key={tid}
                    type="button"
                    onClick={() => setFilters((p) => ({ ...p, type: val }))}
                    aria-pressed={filters.type === val}
                    data-testid={tid}
                    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                      filters.type === val ? "bg-indigo-50 text-indigo-700" : "text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="export-date-from">From</Label>
                <Input
                  id="export-date-from"
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters((p) => ({ ...p, dateFrom: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="export-date-to">To</Label>
                <Input
                  id="export-date-to"
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters((p) => ({ ...p, dateTo: e.target.value }))}
                />
              </div>
            </div>
            <DatePresets
              from={filters.dateFrom}
              to={filters.dateTo}
              onChange={(from, to) => setFilters((p) => ({ ...p, dateFrom: from, dateTo: to }))}
            />
            <p className="text-xs text-gray-400 -mt-2">Both optional — empty means all history.</p>

            <div className="space-y-1.5">
              <Label htmlFor="export-portal">Portal</Label>
              <Select value={toSel(filters.portal)} onValueChange={(v) => setFilters((p) => ({ ...p, portal: fromSel(v) }))}>
                <SelectTrigger id="export-portal">
                  <SelectValue placeholder="All portals" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All portals</SelectItem>
                  {(portalOptions || []).map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="export-fund">Fund</Label>
              <Select value={toSel(filters.fund)} onValueChange={(v) => setFilters((p) => ({ ...p, fund: fromSel(v) }))}>
                <SelectTrigger id="export-fund">
                  <SelectValue placeholder="All funds" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All funds</SelectItem>
                  {(fundOptions || []).map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="export-investor">Member</Label>
              <Select value={toSel(filters.investor)} onValueChange={(v) => setFilters((p) => ({ ...p, investor: fromSel(v) }))}>
                <SelectTrigger id="export-investor">
                  <SelectValue placeholder="All members" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All members</SelectItem>
                  {(investorOptions || []).map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {gList.length > 0 && (
              <div className="space-y-1.5">
                <Label htmlFor="export-group">Group</Label>
                <Select value={toSel(filters.group)} onValueChange={(v) => setFilters((p) => ({ ...p, group: fromSel(v) }))}>
                <SelectTrigger id="export-group">
                  <SelectValue placeholder="All groups" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All groups</SelectItem>
                  {gList.map((g) => (
                    <SelectItem key={g.group_id} value={g.group_id}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              </div>
            )}

            <p className="text-xs text-gray-500 font-medium" data-testid="export-line-count">
              {filteredLines.length} lines will be exported
            </p>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setStep("format")}>
                Back
              </Button>
              <Button type="button" onClick={handleExport} disabled={downloading}>
                {downloading ? "Exporting..." : "Export"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
